/**
 * i18n — 极简翻译模块
 * 默认英文，浏览器语言为中文时默认中文，支持手动切换
 */

import { load, save } from './storage';

export type Lang = 'en' | 'zh';

const LANG_KEY = 'color-picker:lang';

// ============================================================
// 翻译字典
// ============================================================

const en: Record<string, string> = {
  // contrast
  'Contrast': 'Contrast',
  'White BG': 'Light BG',
  'Black BG': 'Dark BG',
  'Aa Text Preview': 'Aa Text Preview',
  // history
  'History': 'History',
  'Favorites': 'Favorites',
  'Removed from favorites': 'Removed from favorites',
  'Saved {hex}': 'Saved {hex}',
  'Clear': 'Clear',
  'Click ⭐ to save current color': 'Click ⭐ to save current color',
  'Colors you pick will appear here': 'Colors you pick will appear here',
  // picker
  '🎯 Eyedropper': '🎯 Eyedropper',
  'EyeDropper API not supported in this browser': 'EyeDropper API not supported in this browser',
  '🖼 From Image': '🖼 From Image',
  'Failed to load image': 'Failed to load image',
  '🎲 Random': '🎲 Random',
  '⭐ Favorite': '⭐ Favorite',
  'Add current color to favorites': 'Add current color to favorites',
  'Canvas not available': 'Canvas not available',
  'Click to pick color · Esc to close': 'Click to pick color · Esc to close',
  // block
  'Copy': 'Copy',
  'Copy {label}': 'Copy {label}',
  'Export': 'Export',
  'HEX Input': 'HEX Input',
  'Copied {value}': 'Copied {value}',
  'Copy failed, please copy manually': 'Copy failed, please copy manually',
  // scheme
  'Complement': 'Complement',
  'Triadic': 'Triadic',
  'Analogous': 'Analogous',
  'Split Comp': 'Split Comp',
  'Shades': 'Shades',
  'Tints': 'Tints',
  'Tones': 'Tones',
  // main
  'Toggle theme': 'Toggle theme',
  'Invalid color format': 'Invalid color format',
  'Copy failed': 'Copy failed',
};

const zh: Record<string, string> = {
  // contrast
  'Contrast': '对比度',
  'White BG': '白底',
  'Black BG': '黑底',
  'Aa Text Preview': 'Aa 文字预览',
  // history
  'History': '历史',
  'Favorites': '收藏',
  'Removed from favorites': '已取消收藏',
  'Saved {hex}': '已收藏 {hex}',
  'Clear': '清空',
  'Click ⭐ to save current color': '点 ⭐ 收藏当前色',
  'Colors you pick will appear here': '取过的色会出现在这里',
  // picker
  '🎯 Eyedropper': '🎯 吸管',
  'EyeDropper API not supported in this browser': '当前浏览器不支持 EyeDropper API',
  '🖼 From Image': '🖼 图片取色',
  'Failed to load image': '图片加载失败',
  '🎲 Random': '🎲 随机',
  '⭐ Favorite': '⭐ 收藏',
  'Add current color to favorites': '把当前色加入收藏',
  'Canvas not available': 'Canvas 不可用',
  'Click to pick color · Esc to close': '点击图片取色 · 按 Esc 退出',
  // block
  'Copy': '复制',
  'Copy {label}': '复制 {label}',
  'Export': '导出',
  'HEX Input': 'HEX 输入',
  'Copied {value}': '已复制 {value}',
  'Copy failed, please copy manually': '复制失败，请手动复制',
  // scheme
  'Complement': '互补',
  'Triadic': '三角',
  'Analogous': '类似',
  'Split Comp': '分裂互补',
  'Shades': '明',
  'Tints': '暗',
  'Tones': '中性',
  // main
  'Toggle theme': '切换主题',
  'Invalid color format': '颜色格式无效',
  'Copy failed': '复制失败',
};

const TRANSLATIONS: Record<Lang, Record<string, string>> = { en, zh };

// ============================================================
// 状态
// ============================================================

let current: Lang;

// ============================================================
// 语言检测 & 初始化
// ============================================================

export function initLang(): Lang {
  const browserLang = navigator.language.toLowerCase().startsWith('zh') ? 'zh' : 'en';
  current = load<Lang>(LANG_KEY, browserLang);
  return current;
}

export function currentLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  save(LANG_KEY, lang);
  for (const fn of langListeners) fn(lang);
}

// ============================================================
// 翻译
// ============================================================

const langListeners: ((lang: Lang) => void)[] = [];

export function onLangChange(fn: (lang: Lang) => void): () => void {
  langListeners.push(fn);
  return () => {
    const idx = langListeners.indexOf(fn);
    if (idx >= 0) langListeners.splice(idx, 1);
  };
}

export function t(key: string, params?: Record<string, string>): string {
  const dict = TRANSLATIONS[current];
  let text = dict[key] ?? TRANSLATIONS.en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(`{${k}}`, v);
    }
  }
  return text;
}
